import React, { useEffect, useRef } from 'react';
import { Users, GraduationCap, Microscope, Mail, ExternalLink, Award, Sparkles } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';

const Recruitment = () => {
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.reveal').forEach((el, index) => {
              setTimeout(() => {
                el.classList.add('active');
              }, index * 100);
            });
          }
        });
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const positions = [
    {
      id: 1,
      title: "Graduate Students",
      icon: GraduationCap,
      description: "We welcome motivated PhD students interested in systems biology, synthetic biology, virology, and computational biology. Students can apply through UCR graduate programs in Microbiology, CMDB, Biomedical Sciences, Genetics, Genomics & Bioinformatics, or Computer Science.",
      requirements: [
        "Molecular biology experience required",
        "Strong background in biology, computational sciences, or related fields",
        "Passion for interdisciplinary research",
        "Interest in viral systems, synthetic circuits, or single-cell technologies"
      ],
      color: "indigo"
    },
    {
      id: 2,
      title: "Postdoctoral Fellows",
      icon: Microscope,
      description: "We seek exceptional postdoctoral researchers to lead independent projects at the interface of systems biology, virology, and translational medicine. Ideal candidates bring expertise in experimental virology, computational biology, or synthetic biology.",
      requirements: [
        "Molecular biology experience required",
        "PhD in biological sciences, computational biology, or related field",
        "Strong publication record",
        "Independent thinking and collaborative spirit"
      ],
      color: "cyan"
    },
    {
      id: 3,
      title: "Undergraduate Researchers",
      icon: Award,
      description: "Undergraduate students at UCR are encouraged to join the lab through research programs, independent study courses, or summer research opportunities. We mentor students interested in gaining hands-on research experience.",
      requirements: [
        "Molecular biology experience required",
        "Currently enrolled at UC Riverside",
        "Commitment to 10-15 hours per week (academic year)",
        "Curiosity and willingness to learn new techniques"
      ],
      color: "amber"
    },
    {
      id: 4,
      title: "Research Staff & Technicians",
      icon: Users,
      description: "We occasionally have openings for research specialists, laboratory managers, and bioinformaticians. These positions offer opportunities to contribute to cutting-edge research while developing technical and professional skills.",
      requirements: [
        "Molecular biology experience required",
        "Bachelor's or Master's degree in relevant field",
        "Experience with cell culture or computational analysis",
        "Strong organizational and communication skills"
      ],
      color: "purple"
    }
  ];

  const colorClasses = {
    indigo: "border-indigo-500 hover:border-indigo-600 bg-indigo-50/50",
    cyan: "border-cyan-500 hover:border-cyan-600 bg-cyan-50/50",
    amber: "border-amber-500 hover:border-amber-600 bg-amber-50/50",
    purple: "border-purple-500 hover:border-purple-600 bg-purple-50/50"
  };

  return (
    <section ref={sectionRef} id="recruitment" className="py-28 bg-white relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-indigo-100/30 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-cyan-100/30 rounded-full blur-3xl"></div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <div className="text-center mb-20 reveal">
          <div className="inline-flex items-center gap-3 px-5 py-2.5 rounded-full glass-light border border-indigo-200 mb-8 shadow-lg shadow-indigo-100/50">
            <Sparkles className="h-5 w-5 text-indigo-600" />
            <span className="text-sm font-bold text-indigo-700 tracking-wide">JOIN US</span>
          </div>
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 mb-6 leading-tight" style={{ fontFamily: "'Space Grotesk', sans-serif", letterSpacing: '-0.02em' }}>
            Join Our <span className="gradient-text">Team</span>
          </h2>
          <div className="w-40 h-2 bg-gradient-to-r from-indigo-600 via-cyan-500 to-amber-500 mx-auto rounded-full shadow-lg mb-6"></div>
          <p className="text-lg md:text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed">
            We are always looking for talented and motivated individuals to join our interdisciplinary team. We recruit at all levels and welcome diverse backgrounds and perspectives.
          </p>
        </div>

        {/* Position Cards */}
        <div className="grid md:grid-cols-2 gap-8 mb-16">
          {positions.map((position, index) => {
            const Icon = position.icon;
            return (
              <Card 
                key={position.id}
                className={`reveal hover-lift glass-light border-l-4 ${colorClasses[position.color]} transition-all duration-300`}
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <CardHeader>
                  <div className="flex items-start gap-4 mb-4">
                    <div className={`p-3 rounded-xl ${position.color === 'indigo' ? 'bg-indigo-100' : position.color === 'cyan' ? 'bg-cyan-100' : position.color === 'amber' ? 'bg-amber-100' : 'bg-purple-100'}`}>
                      <Icon className={`h-6 w-6 ${position.color === 'indigo' ? 'text-indigo-600' : position.color === 'cyan' ? 'text-cyan-600' : position.color === 'amber' ? 'text-amber-600' : 'text-purple-600'}`} />
                    </div>
                    <div className="flex-1">
                      <CardTitle className="text-xl md:text-2xl text-slate-900 mb-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                        {position.title}
                      </CardTitle>
                    </div>
                  </div>
                  <CardDescription className="text-base text-slate-600 leading-relaxed mb-4">
                    {position.description}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <h4 className="font-semibold text-slate-900 mb-3 text-sm uppercase tracking-wide">What We Look For:</h4>
                  <ul className="space-y-2">
                    {position.requirements.map((req, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-slate-600">
                        <span className={`mt-1.5 h-1.5 w-1.5 rounded-full flex-shrink-0 ${position.color === 'indigo' ? 'bg-indigo-500' : position.color === 'cyan' ? 'bg-cyan-500' : position.color === 'amber' ? 'bg-amber-500' : 'bg-purple-500'}`}></span>
                        <span>{req}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* How to Apply Section */}
        <div className="reveal">
          <Card className="glass-light border-2 border-indigo-200 shadow-xl">
            <CardHeader className="text-center pb-6">
              <CardTitle className="text-3xl font-bold text-slate-900 mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                How to Apply
              </CardTitle>
              <CardDescription className="text-lg text-slate-600 max-w-3xl mx-auto">
                If you're interested in joining the Chaturvedi Lab, we'd love to hear from you!
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="p-6 bg-white rounded-xl border border-indigo-100">
                  <h4 className="font-bold text-slate-900 mb-3 flex items-center gap-2">
                    <Mail className="h-5 w-5 text-indigo-600" />
                    Contact Information
                  </h4>
                  <p className="text-sm text-slate-600 mb-4">
                    Please email Dr. Sonali Chaturvedi with your CV, a brief statement of research interests, and any relevant publications or transcripts.
                  </p>
                  <a 
                    href="mailto:sonalic@ucr.edu"
                    className="inline-flex items-center gap-2 px-4 py-2 premium-gradient text-white rounded-lg font-semibold hover:shadow-lg hover:shadow-indigo-500/50 transition-all duration-300 text-sm"
                  >
                    Email Dr. Chaturvedi
                    <ExternalLink className="h-4 w-4" />
                  </a>
                </div>
                <div className="p-6 bg-white rounded-xl border border-indigo-100">
                  <h4 className="font-bold text-slate-900 mb-3 flex items-center gap-2">
                    <GraduationCap className="h-5 w-5 text-cyan-600" />
                    Graduate Program Applications
                  </h4>
                  <p className="text-sm text-slate-600 mb-4">
                    Prospective graduate students should apply through UCR graduate programs and mention Dr. Chaturvedi as a potential advisor.
                  </p>
                  <a 
                    href="https://graduate.ucr.edu/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 glass-light border border-cyan-200 text-cyan-700 rounded-lg font-semibold hover:shadow-lg transition-all duration-300 text-sm"
                  >
                    UCR Graduate Division
                    <ExternalLink className="h-4 w-4" />
                  </a>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default Recruitment;
